import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Helper function to check if token is expired
function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch (e) {
    return true;
  }
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load stored authentication data on mount
  useEffect(() => {
    // Check localStorage first (persistent login)
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');
    
    // If not in localStorage, check sessionStorage (session login)
    const sessionToken = !storedToken ? sessionStorage.getItem('authToken') : null;
    const sessionUser = !storedUser ? sessionStorage.getItem('user') : null;
    
    let currentToken = storedToken || sessionToken;
    let currentUser = storedUser || sessionUser;

    if (currentToken && currentUser) {
      // Check if token is expired
      if (isTokenExpired(currentToken)) {
        // Token is expired, clear storage and state
        logout();
      } else {
        // Token is still valid, set up auth state
        setToken(currentToken);
        setUser(JSON.parse(currentUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      }
    }
    
    setLoading(false);
  }, []);

  // Add axios interceptor to handle token expiration
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // If we get a 401 error, token might be expired
        if (error.response?.status === 401) {
          logout(); // Clear auth state and storage
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Handle login
  async function login(username, password, remember = false) {
    try {
      setError(null);
      setLoading(true);
      
      const response = await axios.post('/api/auth/login', {
        username,
        password
      });

      const { token: newToken, user: userData } = response.data;
      
      // Verify token is valid before proceeding
      if (isTokenExpired(newToken)) {
        throw new Error('Received expired token from server');
      }
      
      // Set auth token in axios defaults
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      // Always store in sessionStorage (for current browser session)
      sessionStorage.setItem('authToken', newToken);
      sessionStorage.setItem('user', JSON.stringify(userData));
      
      // Only store in localStorage if remember is true (persistent)
      if (remember) {
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('user', JSON.stringify(userData));
      }
      
      setToken(newToken);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during login';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }

  // Handle logout
  function logout() {
    // Clear both localStorage and sessionStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }

  // Check if user is authenticated
  function isAuthenticated() {
    return !!token;
  }

  // Update user profile
  async function updateProfile(profileData) {
    try {
      setError(null);
      
      const response = await axios.put('/api/auth/profile', profileData);
      const { user: updatedUser } = response.data;
      
      // Update stored user data
      updateUserStorage(updatedUser);
      
      return { success: true, data: updatedUser };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'An error occurred while updating profile';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  // Helper function to update user storage and state
  function updateUserStorage(userData) {
    // Update localStorage/sessionStorage based on where token was stored
    if (localStorage.getItem('authToken')) {
      localStorage.setItem('user', JSON.stringify(userData));
    }
    if (sessionStorage.getItem('authToken')) {
      sessionStorage.setItem('user', JSON.stringify(userData));
    }
    
    // Update state
    setUser(prevUser => ({
      ...prevUser,
      ...userData
    }));
  }

  // Refresh the user data from the server
  async function refreshUser() {
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      setError(null);
      
      const response = await axios.get('/api/auth/profile');
      const { user: freshUserData } = response.data;
      
      if (freshUserData) {
        console.log('Refreshed user data:', freshUserData);
        updateUserStorage(freshUserData);
      }
      
      return { success: true, data: freshUserData };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to refresh user data';
      console.error('Error refreshing user:', errorMessage);
      setError(errorMessage);
      
      // If we get a 401, clear auth state
      if (err.response?.status === 401) {
        logout();
      }
      
      return { success: false, error: errorMessage };
    }
  }

  // Fetch complete user profile
  async function fetchUserProfile() {
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      setError(null);
      
      const response = await axios.get('/api/auth/profile');
      const { user: fullUserData } = response.data;
      
      // Update stored user data only if we have valid data
      if (fullUserData) {
        updateUserStorage(fullUserData);
      }
      
      return { success: true, data: fullUserData };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'An error occurred while fetching user profile';
      setError(errorMessage);
      
      // If we get a 401, clear auth state
      if (err.response?.status === 401) {
        logout();
      }
      
      return { success: false, error: errorMessage };
    }
  }

  // Delete user profile
  async function deleteProfile() {
    try {
      setError(null);
      setLoading(true);

      const response = await axios.delete('/api/auth/profile');
      const { message } = response.data;

      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);

      return { success: true, message };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'An error occurred while deleting profile';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }

  // Reset user password
  async function resetPassword(email) {
    try {
      setError(null);
      setLoading(true);

      console.log("email",email);

      const response = await axios.post('/api/auth/forgot-password', {
        email: email
      });

      const { message } = response.data;
      return { success: true, message };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'An error occurred while resetting password';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }

  const value = {
    user,
    token,
    loading,
    error,
    login,
    logout,
    isAuthenticated,
    updateProfile,
    fetchUserProfile,
    refreshUser,
    deleteProfile,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 